import { useState } from "react";
import { Table, ConfigProvider } from "antd";
import { useBusSchedulesQuery } from "src/services/react-query-apis/get-bus-schedules";
import ScheduleEditForm from "./schedule-edit-form";
import BasicModal from "src/features/student-attendance/components/modal";
import { getCurrentMonth } from "src/services/auth/token";
import moment from "moment";
import BusScheduleControls from "./bus-schedule-controls";
import { toast } from "react-toastify";
import { usePostBusScheduleMutation } from "src/services/react-query-apis/post-bus-schedule";

const currentMonth = moment(getCurrentMonth()).format("YYYY-MM");
const nextMonth = moment(getCurrentMonth()).add(1, "month").format("YYYY-MM");

const customTheme = {
  components: {
    Table: {
      rowHoverBg: "#D6EAF8",
    },
  },
};

const BusSchedules = () => {
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth || null);

  const busSchedulesQuery = useBusSchedulesQuery({
    month: selectedMonth,
  });

  const rowClassName = r => (r.id === selectedScheduleId ? "selected-row" : "");

  const isNextMonth = selectedMonth === nextMonth;

  const columns = [
    {
      title: "曜日",
      dataIndex: "day_week",
      key: "day_week",
      width: 50,
    },
    {
      title: "往路出発時間",
      dataIndex: "outbound_departure_time",
      key: "outbound_departure_time",
      width: 100,
    },
    {
      title: "復路出発時間",
      dataIndex: "return_departure_time",
      key: "return_departure_time",
      width: 100,
    },
    {
      title: "バスコース",
      dataIndex: "course_code",
      key: "course_code",
      render: (text, record) => {
        return <div>{record.bus_course.code}</div>;
      },
      width: 100,
    },
    {
      title: "往路バス",
      dataIndex: "outbound_bus",
      key: "outbound_bus",
      render: (text, record) => {
        return <div>{record.outbound_bus.name}</div>;
      },
      width: 75,
    },
    {
      title: "往路バス定員",
      dataIndex: "outbound_bus_capacity",
      key: "outbound_bus_capacity",
      render: (text, record) => {
        return <div>{record.outbound_bus.capacity}人</div>;
      },
      width: 75,
    },
    {
      title: "復路バス",
      dataIndex: "return_bus",
      key: "return_bus",
      render: (text, record) => {
        return <div>{record.return_bus.name}</div>;
      },
      width: 75,
    },
    {
      title: "往路バス定員",
      dataIndex: "return_bus_capacity",
      key: "return_bus_capacity",
      render: (text, record) => {
        return <div>{record.return_bus.capacity}人</div>;
      },
      width: 75,
    },
    {
      title: "説明",
      dataIndex: "description",
      key: "description",
      width: 300,
    },
    {
      title: "操作",
      key: "action",
      width: "100px",
      align: "center",
      render: (_, row) => (
        <BusScheduleControls
          schedule={row}
          selectedMonth={selectedMonth}
          showCRUDButtons={isNextMonth}
        />
      ),
    },
  ];

  return (
    <div style={{ width: "80%" }}>
      <div className="card-body">
        <div className="card">
          <div className="card-header d-flex align-items-center">
            <div style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
              バススケジュール
            </div>
            <div className="d-flex align-items-center gap-2 mx-4">
              <label>処理年月:</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                min="2025-07"
              />
            </div>
            {isNextMonth && (
              <CreateScheduleModal selectedMonth={selectedMonth} />
            )}
          </div>
        </div>
        <div style={{ marginTop: "10px" }}>
          <ConfigProvider theme={customTheme}>
            <Table
              bordered
              dataSource={busSchedulesQuery.data?.data || []}
              columns={columns}
              loading={busSchedulesQuery.isLoading}
              rowKey="id"
              pagination={false}
              rowClassName={rowClassName}
              onRow={record => ({
                onClick: () =>
                  setSelectedScheduleId(r =>
                    r === record.id ? null : record.id
                  ),
              })}
              locale={{
                emptyText: busSchedulesQuery.error
                  ? "データの取得に失敗しました"
                  : "データがありません",
              }}
            />
          </ConfigProvider>
        </div>
      </div>
    </div>
  );
};

const CreateScheduleModal = ({ selectedMonth }) => {
  const [openModal, setOpenModal] = useState(false);

  const mutationConfig = {
    onSuccess: async data => {
      toast.success(data.message);
      setOpenModal(false);
    },
  };

  const postBusScheduleMutation = usePostBusScheduleMutation({
    mutationConfig,
  });

  return (
    <>
      <button
        className="btn btn-sm btn-outline-success px-3 ml-auto"
        type="button"
        title="バススケジュール作成"
        onClick={() => setOpenModal(true)}
      >
        <i className="fas fa-plus fa-fw"></i>
      </button>
      <BasicModal
        title="バススケジュール変更"
        isOpen={openModal}
        onCancel={() => setOpenModal(false)}
        width="50%"
      >
        <ScheduleEditForm
          selectedSchedule={null}
          selectedMonth={selectedMonth}
          onSubmit={postBusScheduleMutation.mutate}
          isSubmitting={postBusScheduleMutation.isPending}
          onClose={() => setOpenModal(false)}
        />
      </BasicModal>
    </>
  );
};

export default BusSchedules;
